"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AlertDialog from "./AlertDialog";
import BudgetItemsModal from "./ChangeOrderBudgetItemsModal";
import DatabaseItemsModal from "./ChangeOrderDatabaseItemsModal";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";
const DEFAULT_MARKUP = 30;

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "12px",
  }),
  input: (provided) => ({ ...provided, color: "#e5e7eb" }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#e5e7eb",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 999999 }),
  placeholder: (provided) => ({ ...provided, color: "#9ca3af" }),
};

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

// ─── CO Type definitions ───────────────────────────────────────────────────────
const CO_TYPES = [
  {
    id: "billable",
    label: "Billable change order",
    description:
      "Standard customer-facing CO. You set pricing sections and the customer is charged.",
    activeBorder: "border-orange-500",
    activeBg: "bg-orange-500/10",
    inactiveBorder: "border-gray-600",
    inactiveBg: "bg-gray-800/40",
    badge: "bg-orange-500/20 text-orange-300",
    badgeText: "Billable",
    dot: "bg-orange-400",
  },
  {
    id: "nonbillable",
    label: "Non-billable change order",
    description:
      "Internal cost tracking only. No customer charge. Captures scope variances not billed to client.",
    activeBorder: "border-gray-400",
    activeBg: "bg-gray-500/10",
    inactiveBorder: "border-gray-600",
    inactiveBg: "bg-gray-800/40",
    badge: "bg-gray-500/20 text-gray-300",
    badgeText: "Non-billable",
    dot: "bg-gray-400",
  },
  {
    id: "tandm",
    label: "T&M-based change order",
    description:
      "Cost comes from an approved T&M. You only configure the pricing side. The T&M budget is the cost base.",
    activeBorder: "border-teal-500",
    activeBg: "bg-teal-500/10",
    inactiveBorder: "border-gray-600",
    inactiveBg: "bg-gray-800/40",
    badge: "bg-teal-500/20 text-teal-300",
    badgeText: "T&M Linked",
    dot: "bg-teal-400",
  },
];

const NON_BILLABLE_REASONS = [
  "Site Condition Change",
  "Foreman Field Decision",
  "Estimation Error",
  "Design / Engineering Change",
  "Other",
];

export default function CreateChangeOrderFormV3({ editId = null }) {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  // ── CO type ────────────────────────────────────────────────────────────────
  const [coType, setCoType] = useState("billable"); // "billable" | "nonbillable" | "tandm"
  const [nonBillableReason, setNonBillableReason] = useState("");
  const [nonBillableCustom, setNonBillableCustom] = useState("");

  // ── T&M linking ────────────────────────────────────────────────────────────
  const [eligibleTandMs, setEligibleTandMs] = useState([]);
  const [loadingTandMs, setLoadingTandMs] = useState(false);
  const [selectedTandM, setSelectedTandM] = useState(null); // full object from API

  // ── Form ───────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    title: "",
    customerDescription: "",
    scopeOfWork: "",
    referenceNumber: "",
    termsAndConditions:
      "All work is subject to the terms and conditions of the original contract. This change order is limited strictly to the scope described herein. Any additional work or impacts not expressly identified shall be addressed as a separate change. This change does not include acceleration, premium time, or overtime unless expressly stated. All applicable taxes are extra unless stated otherwise.",
    overheadPercentage: 10,
    defaultMarkupPercentage: DEFAULT_MARKUP,
    changesToSchedule: 0,
  });

  const [summary, setSummary] = useState({
    description: "",
    qtyUnit: "1",
    billable: true,
    revision: 0,
  });

  const [changeItems, setChangeItems] = useState([]);
  const [pricingSections, setPricingSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [vendors, setVendors] = useState([]);

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);

  const [budgetItems, setBudgetItems] = useState([]);
  const [loadingBudgetItems, setLoadingBudgetItems] = useState(false);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState([]);

  const [databaseItems, setDatabaseItems] = useState([]);
  const [loadingDatabaseItems, setLoadingDatabaseItems] = useState(false);
  const [selectedDatabaseItems, setSelectedDatabaseItems] = useState([]);

  const [databaseFilters, setDatabaseFilters] = useState({
    category: [],
    uom: [],
    search: "",
  });

  const [filters, setFilters] = useState({
    category: [],
    vendor: [],
    bidArea: [],
    condition: [],
    search: "",
  });

  const [showFilter, setShowFilter] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
  });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const categoryRef = useRef(null);
  const vendorRef = useRef(null);
  const bidAreaRef = useRef(null);
  const conditionRef = useRef(null);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isSFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return (
      u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
    );
  };
  const isLFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return u === "lf" || u.includes("linear") || u.includes("lin ft");
  };

  const parseSizeToCoverage = (sizeStr, uom) => {
    if (!sizeStr) return null;
    const str = String(sizeStr).trim().toLowerCase();
    const parseLength = (part) => {
      if (!part) return 0;
      const mixedMatch = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
      if (mixedMatch)
        return parseFloat(mixedMatch[1]) + parseFloat(mixedMatch[2]) / 12;
      const feetMatch = part.match(/^([\d.]+)'?$/);
      if (feetMatch) return parseFloat(feetMatch[1]) || 0;
      const inchMatch = part.match(/^([\d.]+)"$/);
      if (inchMatch) return (parseFloat(inchMatch[1]) || 0) / 12;
      const num = parseFloat(part);
      return isNaN(num) ? 0 : num;
    };
    if (isSFUnit(uom)) {
      const parts = str.split(/\s*[xX×]\s*/);
      if (parts.length === 2) {
        const w = parseLength(parts[0]);
        const h = parseLength(parts[1]);
        if (w > 0 && h > 0) return w * h;
      }
      const area = parseLength(parts[0]);
      return area > 0 ? area : null;
    }
    if (isLFUnit(uom)) {
      const length = parseLength(str);
      return length > 0 ? length : null;
    }
    return null;
  };

  const showAlert = (type, title, message, onConfirm) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm:
        onConfirm || (() => setAlert((prev) => ({ ...prev, open: false }))),
    });
  };

  const isFromBudget = (item) =>
    !!item.sourceAssemblyId || !!item.sourceLineItemId;

  // ── Sync coType → summary.billable ────────────────────────────────────────
  useEffect(() => {
    setSummary((prev) => ({ ...prev, billable: coType !== "nonbillable" }));
  }, [coType]);

  // ── Load eligible T&Ms when type switches to tandm ─────────────────────────
  useEffect(() => {
    if (coType !== "tandm") return;
    const load = async () => {
      try {
        setLoadingTandMs(true);
        const res = await axios.get(
          `${API_BASE}/time-and-material2/${projectId}/eligible-for-co`,
          { headers },
        );
        setEligibleTandMs(res.data.tandms || []);
      } catch (err) {
        showAlert("error", "Error", "Failed to load eligible T&Ms");
      } finally {
        setLoadingTandMs(false);
      }
    };
    load();
  }, [coType, projectId]);

  // ── Auto-fill title from selected T&M ─────────────────────────────────────
  useEffect(() => {
    if (selectedTandM && !formData.title) {
      setFormData((prev) => ({
        ...prev,
        title: `T&M #${selectedTandM.timeAndMaterialNumber}${
          selectedTandM.scopeOfWork
            ? " — " + selectedTandM.scopeOfWork.slice(0, 60)
            : ""
        }`,
      }));
    }
  }, [selectedTandM]);

  // ── Reference options ──────────────────────────────────────────────────────
  const referenceOptions = useMemo(
    () => [
      { value: null, label: "— None —" },
      ...pricingSections.map((s) => ({
        value: s.number,
        label: `${s.number} - ${s.title || "Untitled"}`,
      })),
    ],
    [pricingSections],
  );

  const assemblyOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((i) => {
      if (i.conditionName) unique.add(i.conditionName);
    });
    changeItems.forEach((i) => {
      if (i.conditionName) unique.add(i.conditionName);
    });
    return [
      { value: "", label: "— Select Assembly —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, changeItems]);

  const bidAreaOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((i) => {
      if (i.bidArea) unique.add(i.bidArea);
    });
    changeItems.forEach((i) => {
      if (i.bidArea) unique.add(i.bidArea);
    });
    return [
      { value: "", label: "— Select Bid Area —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, changeItems]);

  // ── Section estimated costs ────────────────────────────────────────────────
  const sectionEstimatedCosts = useMemo(() => {
    const costs = {};
    pricingSections.forEach((section) => {
      const items = changeItems.filter(
        (item) => item.referenceNumber === section.number && item.forBudget,
      );
      let mat = 0,
        lab = 0;
      items.forEach((item) => {
        const qty = Math.abs(item.deltaQty || item.quantity || 0);
        const cov = parseFloat(item.coveragePerUnit) || 1;
        const isCov =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const cost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        if (item.category === "M") mat += total;
        else lab += total;
      });
      costs[section.number] = {
        material: Number(mat.toFixed(2)),
        labor: Number(lab.toFixed(2)),
        items,
      };
    });
    return costs;
  }, [changeItems, pricingSections]);

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let budgetCost = 0;
    changeItems.forEach((item) => {
      if (!item.forBudget) return;
      const signedQty = item.deltaQty || item.quantity || 0;
      const qty = Math.abs(signedQty);
      const cov = parseFloat(item.coveragePerUnit) || 1;
      const isCov =
        item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
      const cost =
        item.category !== "M" && item.unitRate > 0
          ? item.unitRate
          : item.unitCost || 0;
      const total = isCov ? qty * cov * cost : qty * cost;
      budgetCost += signedQty < 0 ? -total : total;
    });
    const customerSubtotal = pricingSections.reduce(
      (sum, s) => sum + (s.materialPrice || 0) + (s.laborPrice || 0),
      0,
    );
    const overheadAmount =
      customerSubtotal * ((formData.overheadPercentage || 0) / 100);
    const totalCustomerPrice = customerSubtotal + overheadAmount;
    return {
      budgetCost: Number(budgetCost.toFixed(2)),
      customerSubtotal: Number(customerSubtotal.toFixed(2)),
      overheadAmount: Number(overheadAmount.toFixed(2)),
      totalCustomerPrice: Number(totalCustomerPrice.toFixed(2)),
      margin: Number((customerSubtotal - Math.abs(budgetCost)).toFixed(2)),
    };
  }, [changeItems, pricingSections, formData.overheadPercentage]);

  // ── Click outside ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!showFilter) return;
      const refs = {
        category: categoryRef,
        vendor: vendorRef,
        bidArea: bidAreaRef,
        condition: conditionRef,
      };
      const ref = refs[showFilter];
      if (ref?.current && !ref.current.contains(e.target)) {
        if (
          !e.target.closest('[class*="menu"]') &&
          !e.target.closest('[id^="react-select"]')
        )
          setShowFilter(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showFilter]);

  // ── Initial data fetch ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const vendorsRes = await axios.get(`${API_BASE}/accounts/vendors`, {
          headers,
        });
        setVendors(vendorsRes.data);

        try {
          const [materialsRes, laborRes] = await Promise.all([
            axios.get(
              `${API_BASE}/assemblies/project/${projectId}/materials?unassignedOnly=false&includeClosedOut=false`,
              { headers },
            ),
            axios.get(
              `${API_BASE}/assemblies/project/${projectId}/labor?unassignedOnly=false`,
              { headers },
            ),
          ]);
          const materials = materialsRes.data.map((item) => ({
            ...item,
            category: "M",
            remainingQty: item.remaining || 0,
            budgetedQty: item.requiredQty || 0,
            itemCode: item.materialCode || item._id,
          }));
          const labor = laborRes.data.map((item) => ({
            ...item,
            category: item.category || "L",
            remainingQty: item.quantity || 0,
            budgetedQty: item.quantity || 0,
            itemCode: item.laborId || item._id,
          }));
          setBudgetItems([...materials, ...labor]);
        } catch (budgetErr) {
          console.warn("Could not load budget items for dropdowns:", budgetErr);
        }

        if (editId) {
          const coRes = await axios.get(`${API_BASE}/changeOrders2/${editId}`, {
            headers,
          });
          const co = coRes.data.changeOrder;
          setFormData({
            title: co.title || "",
            customerDescription: co.customerDescription || "",
            scopeOfWork: co.scopeOfWork || "",
            referenceNumber: co.referenceNumber || "",
            termsAndConditions: co.termsAndConditions || "",
            overheadPercentage: co.overheadPercentage || 0,
            defaultMarkupPercentage:
              co.defaultMarkupPercentage || DEFAULT_MARKUP,
            changesToSchedule: co.changesToSchedule || 0,
          });
          setSummary({
            description: co.summary?.description || "",
            qtyUnit: co.summary?.qtyUnit || "1",
            billable: co.summary?.billable ?? true,
            revision: co.summary?.revision || 0,
          });
          setChangeItems(
            (co.items || []).map((item, i) => ({
              ...item,
              _localId: `existing-${i}-${Date.now()}`,
              referenceNumber: item.referenceNumber || null,
            })),
          );
          setPricingSections(co.pricingSections || []);

          // Restore type from saved CO
          if (co.isTandMBased) setCoType("tandm");
          else if (co.summary?.billable === false) setCoType("nonbillable");
          else setCoType("billable");
        }
      } catch (err) {
        console.error(err);
        showAlert("error", "Error", "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, editId]);

  // ── Budget items modal ─────────────────────────────────────────────────────
  const fetchBudgetItems = useCallback(async () => {
    try {
      setLoadingBudgetItems(true);
      const [materialsRes, laborRes, contractLaborRes] = await Promise.all([
        axios.get(
          `${API_BASE}/assemblies/project/${projectId}/materials?unassignedOnly=false&includeClosedOut=false`,
          { headers },
        ),
        axios.get(
          `${API_BASE}/assemblies/project/${projectId}/labor-change-order`,
          { headers },
        ),
        axios.get(`${API_BASE}/projects/${projectId}/contract-labour-items`, {
          headers,
        }),
      ]);
      const materials = materialsRes.data.map((item) => ({
        ...item,
        category: "M",
        remainingQty: item.remaining || 0,
        budgetedQty: item.requiredQty || 0,
        itemCode: item.materialCode || item._id,
      }));
      const labor = laborRes.data.map((item) => ({
        ...item,
        category: item.category || "L",
        remainingQty: item.quantity || 0,
        laborId: item.laborId,
        budgetedQty: item.quantity || 0,
        itemCode: item.itemCode || "",
      }));
      const contractLabor = contractLaborRes.data.map((item) => ({
        ...item,
        category: "L",
        remainingQty: item.quantity || 0,
        budgetedQty: item.quantity || 0,
        itemCode:
          item.labourItem?.itemCode || `CL-${item._id.toString().slice(-8)}`,
        conditionName: "",
        sectionNumber: item.costCode?.code?.split("-")[0]?.trim() || "",
        bidArea: "",
        unitRate: item.unitCost || item.labourItem?.unitCost || 0,
        uom: item.uom || item.labourItem?.uom || "HR",
        itemDescription:
          item.title || item.labourItem?.title || "Contract Labor",
        costCodeDescription: item.title || item.labourItem?.title || "",
        isContractLabor: true,
        contractLabourItemId: item._id,
      }));
      setBudgetItems([...materials, ...labor, ...contractLabor]);
    } catch (err) {
      console.error(err);
      showAlert("error", "Error", "Failed to load budget items");
    } finally {
      setLoadingBudgetItems(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (showBudgetModal) fetchBudgetItems();
  }, [showBudgetModal, fetchBudgetItems]);

  const getFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      budgetItems.forEach((item) => {
        let value;
        if (field === "category") value = item.category;
        if (field === "vendor") value = item.vendor?._id || item.vendor;
        if (field === "bidArea") value = item.bidArea;
        if (field === "condition") value = item.conditionName;
        if (field === "uom") value = item.uom || item.container;
        if (value) unique.add(value);
      });
      if (field === "vendor") {
        return Array.from(unique).map((id) => ({
          value: id,
          label: vendors.find((v) => v._id === id)?.companyName || "Unknown",
        }));
      }
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v || "N/A" }));
    },
    [budgetItems, vendors],
  );

  const addBudgetItemsToChangeOrder = () => {
    if (selectedBudgetItems.length === 0) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }
    const getKey = (item) =>
      `${item.itemCode || item._id}-${item.sourceAssemblyId || item.assemblyId || ""}-${item.bidArea || ""}`;
    const existingKeys = new Set(changeItems.map(getKey));
    const dupes = selectedBudgetItems.filter((i) =>
      existingKeys.has(getKey(i)),
    );
    if (dupes.length > 0) {
      showAlert(
        "error",
        "Duplicate Items",
        `Already added: ${dupes.map((d) => d.itemCode).join(", ")}`,
      );
      return;
    }
    const nextItemNum = changeItems.length + 1;
    const newItems = selectedBudgetItems.map((item, idx) => {
      let rawDelta = item.deltaQty || 1;
      const remaining = item.remainingQty || item.remaining || 0;
      const budgeted =
        item.budgetedQty || item.requiredQty || item.quantity || remaining;
      if (rawDelta < -budgeted) rawDelta = -budgeted;
      const qty = Math.abs(rawDelta);
      const isMaterial = item.category === "M";
      const materialCost = item.cost || item.price || 0;
      const laborRate = item.unitRate || item.unitCost || item.price || 0;
      const unitCost = isMaterial
        ? Number(materialCost.toFixed(2))
        : Number(laborRate.toFixed(2));
      const unitRate = isMaterial ? 0 : Number(laborRate.toFixed(2));
      let coverage = 1;
      if (isMaterial && item.size) {
        const parsed = parseSizeToCoverage(item.size, item.uom);
        if (parsed !== null && parsed > 0) coverage = parsed;
      }
      if (coverage === 1 && item.coveragePerUnit) {
        const existing = parseFloat(item.coveragePerUnit);
        if (!isNaN(existing) && existing > 0) coverage = existing;
      }
      let displaySize = item.size || "";
      if (
        isMaterial &&
        coverage === 1 &&
        (isSFUnit(item.uom) || isLFUnit(item.uom))
      ) {
        displaySize = isSFUnit(item.uom) ? "1' x 1'" : "1'";
      }
      const baseAmount = isMaterial
        ? qty * coverage * unitCost
        : qty * unitRate;
      return {
        _localId: `budget-${Date.now()}-${idx}`,
        itemNumber: String(nextItemNum + idx),
        sourceAssemblyId: item.assemblyId,
        sourceLineItemId: item._id,
        sectionNumber: item.sectionNumber,
        itemCode: item.itemCode || item.materialCode || item.laborCode || "",
        type: isMaterial ? "material" : "labor",
        category: item.category,
        contractLabourItemId: item.contractLabourItemId || null,
        isFromDatabase: false,
        title: item.itemDescription || item.costCodeDescription || "",
        itemDescription: item.itemDescription || item.costCodeDescription || "",
        customerDescription:
          item.itemDescription || item.costCodeDescription || "",
        conditionName: item.conditionName || "",
        laborId: item.laborId,
        bidArea: item.bidArea || "",
        deltaQty: rawDelta,
        quantity: qty,
        unitCost,
        unitRate,
        amount: rawDelta < 0 ? -baseAmount : baseAmount,
        size: displaySize,
        uom: item.uom || "",
        coveragePerUnit: String(coverage),
        vendor: item.vendor?._id || item.vendor || null,
        remainingQty: remaining,
        budgetedQty: budgeted,
        forBudget: true,
        referenceNumber: null,
      };
    });
    setChangeItems((prev) => [...prev, ...newItems]);
    setSelectedBudgetItems([]);
    setShowBudgetModal(false);
    showAlert("success", "Success", `Added ${newItems.length} item(s)`);
  };

  // ── Database items modal ───────────────────────────────────────────────────
  const fetchDatabaseItems = useCallback(async () => {
    try {
      setLoadingDatabaseItems(true);
      const res = await axios.get(`${API_BASE}/item-database`, { headers });
      setDatabaseItems(res.data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDatabaseItems(false);
    }
  }, []);

  useEffect(() => {
    if (showDatabaseModal && databaseItems.length === 0) fetchDatabaseItems();
  }, [showDatabaseModal, fetchDatabaseItems, databaseItems.length]);

  const getDatabaseFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      databaseItems.forEach((item) => {
        const v = field === "uom" ? item.uom : item[field];
        if (v) unique.add(v);
      });
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v }));
    },
    [databaseItems],
  );

  const addDatabaseItemsToChangeOrder = () => {
    if (selectedDatabaseItems.length === 0) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }
    const getKey = (item) =>
      `${item.itemCode}-${item.sourceAssemblyId || ""}-${item.bidArea || ""}`;
    const existingKeys = new Set(changeItems.map(getKey));
    const dupes = selectedDatabaseItems.filter((sel) => {
      const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
      if (!item) return false;
      return existingKeys.has(getKey({ ...item, bidArea: sel.bidArea || "" }));
    });
    if (dupes.length > 0) {
      showAlert(
        "error",
        "Duplicate Items",
        `Already added: ${dupes.map((d) => d.itemCode).join(", ")}`,
      );
      return;
    }
    const nextItemNum = changeItems.length + 1;
    const newItems = selectedDatabaseItems
      .map((sel, idx) => {
        const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
        if (!item) return null;
        let unitCost = item.defaultCost || 0;
        if (sel.selectedVendor && Array.isArray(item.vendorPrices)) {
          const vp = item.vendorPrices.find(
            (p) => p.vendor?._id === sel.selectedVendor,
          );
          if (vp) unitCost = vp.price || 0;
        }
        const qty = Math.abs(sel.deltaQty || 1);
        const coverage =
          sel.editedCoverage || parseFloat(item.coveragePerUnit) || 1;
        const uom = item.uom || "";
        const isCov = item.category === "M" && (isSFUnit(uom) || isLFUnit(uom));
        let size = sel.editedSize?.trim() || item.size?.trim() || "";
        if (!size && isCov && coverage === 1)
          size = isSFUnit(uom) ? "1' x 1'" : "1'";
        const amount = isCov
          ? Number((qty * coverage * unitCost).toFixed(2))
          : Number((qty * unitCost).toFixed(2));
        return {
          _localId: `database-${Date.now()}-${idx}`,
          itemNumber: String(nextItemNum + idx),
          itemDatabaseId: item._id,
          isFromDatabase: true,
          itemCode: sel.itemCode,
          type: item.category === "M" ? "material" : "labor",
          category: item.category,
          sectionNumber: item.sectionNumber || "",
          title: item.description || "",
          itemDescription: item.description || "",
          customerDescription: item.description || "",
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          deltaQty: sel.deltaQty || 1,
          quantity: qty,
          unitCost,
          amount,
          size,
          uom,
          coveragePerUnit: String(coverage),
          vendor: sel.selectedVendor || null,
          forBudget: true,
          referenceNumber: null,
        };
      })
      .filter(Boolean);
    setChangeItems((prev) => [...prev, ...newItems]);
    setSelectedDatabaseItems([]);
    setShowDatabaseModal(false);
    setDatabaseFilters({ category: [], uom: [], search: "" });
    showAlert("success", "Success", `Added ${newItems.length} item(s)`);
  };

  // ── Item handlers ──────────────────────────────────────────────────────────
  const handleUpdateItem = (localId, field, value) => {
    setChangeItems((prev) =>
      prev.map((item) => {
        if (item._localId !== localId) return item;
        const updated = { ...item };
        if (field === "quantity" || field === "deltaQty") {
          let newDelta = parseFloat(value) || 0;
          if (isFromBudget(item)) {
            const budgeted = item.budgetedQty || item.remainingQty || 0;
            if (newDelta < -budgeted) newDelta = -budgeted;
          } else {
            if (newDelta < 0) newDelta = 0;
          }
          updated.deltaQty = newDelta;
          updated.quantity = Math.abs(newDelta);
        } else {
          updated[field] = value;
          if (field === "size") {
            const c = parseSizeToCoverage(value, updated.uom);
            if (c !== null) updated.coveragePerUnit = String(c);
          }
        }
        const qty = Math.abs(updated.deltaQty || 1);
        const cov = parseFloat(updated.coveragePerUnit) || 1;
        const isCov =
          updated.category === "M" &&
          (isSFUnit(updated.uom) || isLFUnit(updated.uom));
        const cost =
          updated.category !== "M" && updated.unitRate > 0
            ? updated.unitRate
            : updated.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        updated.amount = (updated.deltaQty || 0) < 0 ? -total : total;
        return updated;
      }),
    );
  };

  const handleRemoveItem = (localId) =>
    setChangeItems((prev) => prev.filter((i) => i._localId !== localId));

  // ── Pricing section handlers ───────────────────────────────────────────────
  const addPricingSection = () => {
    const n = pricingSections.length + 1;
    setPricingSections((prev) => [
      ...prev,
      { number: n, title: "", materialPrice: 0, laborPrice: 0 },
    ]);
    setExpandedSections((prev) => new Set([...prev, n]));
  };

  const updatePricingSection = (index, field, value) =>
    setPricingSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );

  const removePricingSection = (index) => {
    const removedNumber = pricingSections[index].number;
    setChangeItems((prev) =>
      prev.map((item) =>
        item.referenceNumber === removedNumber
          ? { ...item, referenceNumber: null }
          : item,
      ),
    );
    setPricingSections((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, number: i + 1 })),
    );
    setChangeItems((prev) =>
      prev.map((item) =>
        item.referenceNumber > removedNumber
          ? { ...item, referenceNumber: item.referenceNumber - 1 }
          : item,
      ),
    );
  };

  const toggleSectionExpand = (index) => {
    const n = pricingSections[index].number;
    setExpandedSections((prev) => {
      const s = new Set(prev);
      s.has(n) ? s.delete(n) : s.add(n);
      return s;
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: [
        "overheadPercentage",
        "defaultMarkupPercentage",
        "changesToSchedule",
      ].includes(name)
        ? parseFloat(value) || 0
        : value,
    }));
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const canSubmit = () => {
    if (!formData.title.trim()) return false;
    if (coType === "tandm" && !selectedTandM) return false;
    if (coType === "nonbillable" && !nonBillableReason) return false;
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) {
      if (coType === "tandm" && !selectedTandM) {
        showAlert("error", "Missing T&M", "Please select a T&M to link");
        return;
      }
      if (coType === "nonbillable" && !nonBillableReason) {
        showAlert(
          "error",
          "Missing Reason",
          "Please select a reason for the non-billable CO",
        );
        return;
      }
      showAlert(
        "error",
        "Missing Fields",
        "Please fill in all required fields",
      );
      return;
    }

    try {
      setIsSaving(true);

      const effectiveReason =
        nonBillableReason === "Other" ? nonBillableCustom : nonBillableReason;

      const items = changeItems.map(({ _localId, ...item }) => item);
      const payload = {
        title: formData.title,
        project: projectId,
        items: coType === "tandm" ? [] : items, // T&M-based COs have no CO-side budget items on creation
        pricingSections,
        referenceNumber: formData.referenceNumber,
        overheadPercentage: formData.overheadPercentage,
        defaultMarkupPercentage: formData.defaultMarkupPercentage,
        changesToSchedule: formData.changesToSchedule,
        termsAndConditions: formData.termsAndConditions,
        customerDescription: formData.customerDescription,
        scopeOfWork: formData.scopeOfWork,
        summary: {
          ...summary,
          billable: coType !== "nonbillable",
          description:
            coType === "nonbillable"
              ? effectiveReason || ""
              : summary.description,
        },
      };

      let savedCO;
      if (editId) {
        const res = await axios.put(
          `${API_BASE}/changeOrders2/${editId}`,
          payload,
          { headers },
        );
        savedCO = res.data.changeOrder;
      } else {
        const res = await axios.post(`${API_BASE}/changeOrders2`, payload, {
          headers,
        });
        savedCO = res.data.changeOrder;
      }

      // Link T&M if type is tandm and we have a selection (and not already linked)
      if (coType === "tandm" && selectedTandM && !editId) {
        await axios.put(
          `${API_BASE}/changeOrders2/${savedCO._id}/link-tandm`,
          { tandmId: selectedTandM._id, projectId },
          { headers },
        );
      }

      showAlert(
        "success",
        "Success",
        editId ? "Change order updated" : "Change order created",
        () =>
          navigate(`/projects/${projectId}`, {
            state: { initialTab: "Change Order (CO)" },
          }),
      );
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── T&M options for Select ─────────────────────────────────────────────────
  const tandmSelectOptions = eligibleTandMs.map((t) => ({
    value: t._id,
    label: t.label,
    data: t,
  }));

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        onConfirm={alert.onConfirm}
      />

      <BudgetItemsModal
        open={showBudgetModal}
        onClose={() => {
          setShowBudgetModal(false);
          setSelectedBudgetItems([]);
        }}
        budgetItems={budgetItems}
        loading={loadingBudgetItems}
        selectedItems={selectedBudgetItems}
        setSelectedItems={setSelectedBudgetItems}
        onAddItems={addBudgetItemsToChangeOrder}
        formatCurrency={formatCurrency}
        getFilterOptions={getFilterOptions}
      />

      <DatabaseItemsModal
        open={showDatabaseModal}
        onClose={() => {
          setShowDatabaseModal(false);
          setSelectedDatabaseItems([]);
          setDatabaseFilters({ category: [], uom: [], search: "" });
        }}
        databaseItems={databaseItems}
        loading={loadingDatabaseItems}
        databaseFilters={databaseFilters}
        setDatabaseFilters={setDatabaseFilters}
        selectedDatabaseItems={selectedDatabaseItems}
        setSelectedDatabaseItems={setSelectedDatabaseItems}
        onAddItems={addDatabaseItemsToChangeOrder}
        formatCurrency={formatCurrency}
        formData={formData}
        parseSizeToCoverage={parseSizeToCoverage}
        isSFUnit={isSFUnit}
        isLFUnit={isLFUnit}
        getDatabaseFilterOptions={getDatabaseFilterOptions}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Header ── */}
        <div className="mb-6 shadow-lg bg-gray-700 rounded-lg p-6 border border-gray-600">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-white">
              {editId ? "Edit Change Order" : "Create Change Order"}
            </h1>
            <button
              type="button"
              onClick={() =>
                navigate(`/projects/${projectId}`, {
                  state: { initialTab: "Change Order (CO)" },
                })
              }
              className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500"
            >
              Back
            </button>
          </div>
        </div>

        {/* ── CO Type Selection ── */}
        {!editId && (
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <h2 className="text-lg font-semibold text-white mb-3">
              Change Order Type
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {CO_TYPES.map((type) => {
                const active = coType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setCoType(type.id);
                      setSelectedTandM(null);
                    }}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                      active
                        ? `${type.activeBorder} ${type.activeBg}`
                        : `${type.inactiveBorder} ${type.inactiveBg} hover:border-gray-500`
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${active ? "border-white" : "border-gray-500"}`}
                        >
                          {active && (
                            <div
                              className={`w-2 h-2 rounded-full ${type.dot}`}
                            />
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-200">
                          {type.label}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-0.5 text-[10px] rounded-full font-medium flex-shrink-0 ${type.badge}`}
                      >
                        {type.badgeText}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 ml-5">
                      {type.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* T&M selector */}
            {coType === "tandm" && (
              <div className="mt-4 p-4 bg-teal-900/20 rounded-lg border border-teal-600/30">
                <label className="block text-sm font-medium text-teal-300 mb-2">
                  Select approved T&M to link{" "}
                  <span className="text-red-400">*</span>
                </label>
                {loadingTandMs ? (
                  <div className="text-sm text-gray-400 py-2">
                    Loading approved T&Ms...
                  </div>
                ) : eligibleTandMs.length === 0 ? (
                  <div className="text-sm text-yellow-300 bg-yellow-900/20 rounded px-3 py-2.5 border border-yellow-600/30">
                    No approved T&Ms are available. A T&M must be Approved, have
                    an approved budget, and not already be linked to another CO.
                  </div>
                ) : (
                  <>
                    <Select
                      options={tandmSelectOptions}
                      value={
                        selectedTandM
                          ? {
                              value: selectedTandM._id,
                              label: selectedTandM.label,
                              data: selectedTandM,
                            }
                          : null
                      }
                      onChange={(opt) => setSelectedTandM(opt?.data || null)}
                      placeholder="Select an approved T&M..."
                      isClearable
                      styles={customStyles}
                      menuPortalTarget={document.body}
                    />
                    {selectedTandM && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="bg-gray-800/60 rounded px-3 py-2">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            T&M #
                          </p>
                          <p className="text-sm text-teal-300 font-medium">
                            {selectedTandM.timeAndMaterialNumber}
                          </p>
                        </div>
                        <div className="bg-gray-800/60 rounded px-3 py-2">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            Budget Cost
                          </p>
                          <p className="text-sm text-teal-300 font-medium">
                            {formatCurrency(selectedTandM.budgetCost)}
                          </p>
                        </div>
                        <div className="bg-gray-800/60 rounded px-3 py-2">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            Approved batches
                          </p>
                          <p className="text-sm text-teal-300 font-medium">
                            {selectedTandM.approvedEntryCount}
                          </p>
                        </div>
                        {selectedTandM.scopeOfWork && (
                          <div className="col-span-3 bg-gray-800/60 rounded px-3 py-2">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                              Scope
                            </p>
                            <p className="text-xs text-gray-300">
                              {selectedTandM.scopeOfWork}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Non-billable reason */}
            {coType === "nonbillable" && (
              <div className="mt-4 p-4 bg-gray-800/40 rounded-lg border border-gray-600">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reason for non-billable CO{" "}
                  <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-3">
                  <select
                    value={nonBillableReason}
                    onChange={(e) => {
                      setNonBillableReason(e.target.value);
                      if (e.target.value !== "Other") setNonBillableCustom("");
                    }}
                    className="flex-1 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-2"
                  >
                    <option value="">— Select reason —</option>
                    {NON_BILLABLE_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {nonBillableReason === "Other" && (
                    <input
                      type="text"
                      value={nonBillableCustom}
                      onChange={(e) => setNonBillableCustom(e.target.value)}
                      placeholder="Describe reason..."
                      className="flex-1 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-2"
                      autoFocus
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Change Order Details ── */}
        <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-3">
            Change Order Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-400">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Client Ref #
              </label>
              <input
                type="text"
                name="referenceNumber"
                value={formData.referenceNumber}
                onChange={handleFormChange}
                placeholder="e.g. PCO-001"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Overhead %
              </label>
              <input
                type="number"
                name="overheadPercentage"
                value={formData.overheadPercentage}
                onChange={handleFormChange}
                step="0.01"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Markup %
              </label>
              <input
                type="number"
                name="defaultMarkupPercentage"
                value={formData.defaultMarkupPercentage}
                onChange={handleFormChange}
                step="0.01"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Schedule (Days)
              </label>
              <input
                type="number"
                name="changesToSchedule"
                value={formData.changesToSchedule}
                onChange={handleFormChange}
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-400">
              Customer Description (for PDF)
            </label>
            <textarea
              name="customerDescription"
              value={formData.customerDescription}
              onChange={handleFormChange}
              rows="2"
              className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-400">
              Scope of Work
            </label>
            <textarea
              name="scopeOfWork"
              value={formData.scopeOfWork}
              onChange={handleFormChange}
              rows="2"
              className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
            />
          </div>
        </div>

        {/* T&M-based cost info banner */}
        {coType === "tandm" && selectedTandM && (
          <div className="bg-gray-900 border border-teal-600/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-gray-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-3 h-3 text-teal-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-teal-300">
                  Cost tracked via T&M #{selectedTandM.timeAndMaterialNumber}
                </p>
                <p className="text-xs text-teal-500 mt-1">
                  Budget cost items are managed through the linked T&M. Once
                  this CO is created you will be able to view the full cost
                  breakdown in the CO editor. Set your pricing sections below to
                  configure what the customer is charged.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Summary ── */}
        <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {coType !== "tandm" && (
              <div className="bg-gray-600 p-3 rounded">
                <p className="text-xs text-gray-400">Budget Cost</p>
                <p
                  className={`text-lg font-semibold ${totals.budgetCost >= 0 ? "text-green-400" : "text-red-400"}`}
                >
                  {formatCurrency(totals.budgetCost)}
                </p>
              </div>
            )}
            {coType === "tandm" && selectedTandM && (
              <div className="bg-teal-900/30 p-3 rounded border border-teal-600/30">
                <p className="text-xs text-teal-500">T&M Budget Cost</p>
                <p className="text-lg font-semibold text-teal-300">
                  {formatCurrency(selectedTandM.budgetCost)}
                </p>
              </div>
            )}
            <div className="bg-gray-600 p-3 rounded">
              <p className="text-xs text-gray-400">Customer Subtotal</p>
              <p className="text-lg font-semibold text-gray-200">
                {formatCurrency(totals.customerSubtotal)}
              </p>
            </div>
            <div className="bg-gray-600 p-3 rounded">
              <p className="text-xs text-gray-400">
                Overhead ({formData.overheadPercentage}%)
              </p>
              <p className="text-lg font-semibold text-gray-200">
                {formatCurrency(totals.overheadAmount)}
              </p>
            </div>
            <div className="bg-gray-600 p-3 rounded">
              <p className="text-xs text-gray-400">Customer Total</p>
              <p className="text-lg font-semibold text-orange-400">
                {formatCurrency(totals.totalCustomerPrice)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Internal Notes
              </label>
              <textarea
                value={summary.description}
                onChange={(e) =>
                  setSummary((p) => ({ ...p, description: e.target.value }))
                }
                rows="2"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Terms and Conditions
              </label>
              <textarea
                name="termsAndConditions"
                value={formData.termsAndConditions}
                onChange={handleFormChange}
                rows="2"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
          </div>

          {/* Billable toggle — only shown for non-tandm types where it's not auto-set */}
          {coType !== "tandm" && coType !== "nonbillable" && (
            <div className="flex items-center gap-3 mb-5">
              <span className="text-sm text-gray-400">Billable:</span>
              <button
                type="button"
                onClick={() =>
                  setSummary((p) => ({ ...p, billable: !p.billable }))
                }
                className={`relative w-14 h-7 rounded-full transition-colors ${summary.billable ? "bg-orange-500" : "bg-gray-500"}`}
              >
                <span
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${summary.billable ? "translate-x-1" : "-translate-x-5"}`}
                />
              </button>
              <span className="text-sm text-gray-300">
                {summary.billable ? "Yes" : "No"}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() =>
                navigate(`/projects/${projectId}`, {
                  state: { initialTab: "Change Order (CO)" },
                })
              }
              className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !canSubmit()}
              className={`px-6 py-2 text-sm text-white rounded font-medium ${
                isSaving || !canSubmit()
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {isSaving
                ? "Saving..."
                : editId
                  ? "Save Change Order"
                  : "Create Change Order"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
